import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, IconButton, Typography, Divider, Grid, Chip, Card, TextField, CircularProgress, MenuItem, Button, Select, FormControl, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert } from '@mui/material';
import { ArrowBack, Edit, Delete, CalendarMonth, Timer, TaskAlt, Bookmark, Description, AccountTree, Flag, DonutLarge, Save, ChatBubbleOutline, Workspaces, RateReview, HistoryEdu } from '@mui/icons-material';
import { useNavigate, useSearchParams } from "react-router-dom";
import { deleteData, getData, putData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';
import { getStatusColor, priorityColors, getAvatarColor } from '../../../../utils/statusColor';
import Loader from '../../../../common/spinner';

function UserSubTaskDetails() {
    const navigate = useNavigate();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const [searchParams] = useSearchParams();
    const subTaskId = searchParams.get("subTaskId");
    const [loading, setLoading] = useState(true);
    const [taskData, setTaskData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const [formData, setFormData] = useState({
        task_id: subTaskId,
        employee_id: userId,
        comment: '',
        status: '',
        priority: '',
        progress: 0,
        reason: ''
    });

    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [errors, setErrors] = useState({});

    const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

    const fetchDetails = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getData(API_ENDPOINTS.fetchTaskById(subTaskId));
            const task = res.result;

            if (task) {
                setTaskData(task);
                setFormData(prev => ({
                    ...prev,
                    title: task.task_title || '',
                    description: task.description || '',
                    priority: task.priority || '',
                    status: task.status || '',
                    progress: task.progress || 0,
                    comment: '',
                    reason: '' // Clear reason for new update
                }));
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to fetch task details', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [subTaskId]);

    useEffect(() => {
        if (subTaskId) fetchDetails();
    }, [subTaskId, fetchDetails]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updatedValue = value;

        if ((name === 'comment' || name === 'reason') && updatedValue.length > 0) {
            updatedValue = updatedValue.charAt(0).toUpperCase() + updatedValue.slice(1);
        }

        if (name === 'status') {
            if (value === 'On Hold') updatedValue = 'Hold';
            if (value === 'Reopen') updatedValue = 'Re Open'; // optional safety
        }

        setFormData(prev => {
            let updatedData = { ...prev, [name]: updatedValue };

            if (name === 'status') {
                if (updatedValue === 'Completed') {
                    updatedData.progress = 100;
                } else {
                    updatedData.progress = 0;
                }

                setErrors(prevErr => ({ ...prevErr, reason: false }));
            }

            if (name === 'reason' && updatedValue.trim()) {
                setErrors(prevErr => ({ ...prevErr, reason: false }));
            }

            return updatedData;
        });
    };

    const handleUpdate = async () => {
        const needsReason = ['Hold', 'Re Open'].includes(formData.status);

        if (needsReason && !formData.reason.trim()) {
            setErrors({ reason: true });
            const displayStatus = (status) => {
                if (status === 'Hold') return 'On Hold';
                return status;
            };
            setSnackbar({
                open: true,
                message: `Reason is mandatory for ${displayStatus} status`,
                severity: 'warning'
            });
            return;
        }

        setIsUpdating(true);
        try {
            const assigneeIds = taskData.assignees?.map(a => a.id) || [];

            const payload = {
                id: parseInt(subTaskId),
                task_title: formData.title,
                status: formData.status,
                progress: parseInt(formData.progress),
                priority: formData.priority,
                assignees: assigneeIds,
                comments: formData.comment.trim() ? [formData.comment] : [],
                reason: formData.reason.trim(),
                user_id: parseInt(userId),
                user_type: userRole,
            };

            const res = await putData(API_ENDPOINTS.updateUserTaskById, payload);

            if (res.status === "SUCCESS" || res.data?.status === "SUCCESS") {
                setSnackbar({ open: true, message: 'Task updated successfully', severity: 'success' });
                setIsEditing(false);
                setFormData(prev => ({ ...prev, comment: '', reason: '' }));
                fetchDetails();
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Update failed', severity: 'error' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            // Corrected: Uses backticks and passes ID as a URL parameter
            const res = await deleteData(`${API_ENDPOINTS.deleteUserTaskById}/${subTaskId}`);

            if (res.status === "SUCCESS") {
                setSnackbar({ open: true, message: 'Task deleted successfully', severity: 'success' });
                setTimeout(() => navigate(-1), 600);
            }
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || err.message,
                severity: 'error'
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false); // Close the modal after attempt
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Loader /></Box>;
    if (!taskData) return <Typography sx={{ p: 3, textAlign: 'center' }}>Task not found</Typography>;

    const metaItems = [
        { label: 'Project', val: taskData.Project?.project_title, icon: <AccountTree fontSize="small" /> },
        { label: 'Task Code', val: taskData.task_code, icon: <Bookmark fontSize="small" /> },
        { label: 'Task Type', val: taskData.task_type, icon: <TaskAlt fontSize="small" /> },
        { label: 'Work Type', val: taskData.work_type, icon: <Workspaces fontSize="small" /> },
        {
            label: 'Timeline',
            val: `${formatDate(taskData.start_date)} - ${formatDate(taskData.due_date)}`,
            icon: <CalendarMonth fontSize="small" />
        },
        { label: 'Estimated', val: `${taskData.estimated_hours || 0} hrs`, icon: <Timer fontSize="small" /> },
        {
            label: 'Priority',
            val: taskData.priority,
            icon: <Flag fontSize="small" />,
            isEditable: true,
            name: 'priority',
            options: ['Low', 'Medium', 'High'],
            colors: priorityColors[taskData.priority] || { bg: '#f1f5f9', text: '#64748b' }
        },
        {
            label: 'Status',
            // Map 'Hold' to 'On Hold' for the label, but keep raw status for logic
            val: taskData.status === 'Hold' ? 'On Hold' : taskData.status,
            icon: <DonutLarge fontSize="small" />,
            isEditable: true,
            name: 'status',
            // Include 'Overdue' in the logic-base options
            options: ['Todo', 'In Progress', 'Completed', 'On Hold', 'Re Open', 'Overdue'],
            // Get full style object (bg, color, border) from utility
            colors: getStatusColor(taskData.status)
        },
    ];

    function formatDate(dateString) {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    return (
        <>
            {/* Header */}
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', px: 2, py: 1 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: '#64748b', borderRadius: '8px' }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 3, height: 32 }} />
                <Box sx={{ flexGrow: 1 }}><Typography variant="h6" sx={{ fontWeight: 700 }}>Sub-Task Details</Typography></Box>
                {isEditing && (
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" size="small" startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleUpdate} sx={{ bgcolor: '#10b981', textTransform: 'none' }}>Save Changes</Button>
                        <Button variant="outlined" size="small" onClick={() => { setIsEditing(false); fetchDetails(); }} sx={{ textTransform: 'none' }}>Cancel</Button>
                    </Stack>
                )}
            </Stack>

            <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8.5 }}>
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    <span style={{ color: '#3b82f6' }}>#{taskData.task_code}</span> {taskData.task_title}
                                </Typography>
                                {!isEditing && (
                                    <Stack direction="row">
                                        <IconButton onClick={() => setIsEditing(true)}><Edit sx={{ color: '#f59e0b' }} /></IconButton>
                                        <IconButton onClick={() => setShowDeleteModal(true)}><Delete sx={{ color: '#ef4444' }} /></IconButton>
                                    </Stack>
                                )}
                            </Box>

                            <Grid container spacing={2}>
                                {/* Locate this section in your return statement */}
                                {metaItems.map((item, idx) => (
                                    <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            {/* Standardize Icon Color based on the utility color */}
                                            <Box sx={{ color: item.isEditable ? item.colors?.color : '#94a3b8', display: 'flex' }}>
                                                {item.icon}
                                            </Box>

                                            <Typography variant="body2" sx={{ color: '#64748b', minWidth: 100 }}>
                                                {item.label}
                                            </Typography>

                                            {isEditing && item.isEditable ? (
                                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                                    <Select
                                                        name={item.name}
                                                        value={(item.name === 'status' && formData[item.name] === 'Hold') ? 'On Hold' : (formData[item.name] || '')}
                                                        onChange={handleInputChange}
                                                        sx={{ height: 32, fontSize: '0.875rem' }}
                                                    >
                                                        {item.options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                item.isEditable ? (
                                                    /* Apply the utility styles here for Priority and Status Chips */
                                                    <Chip
                                                        label={item.val}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: item.colors?.bg,
                                                            color: item.colors?.color,
                                                            fontWeight: 600,
                                                            px: 1,
                                                            border: `1px solid ${item.colors?.border}` // The fix for the border
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {item.val}
                                                    </Typography>
                                                )
                                            )}
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* MANDATORY REASON INPUT (While Editing) */}
                            {/* Check for both versions to be safe, or normalize to your internal state keys */}
                            {isEditing && (formData.status === 'Hold' || formData.status === 'On Hold' || formData.status === 'Re Open') && (
                                <Card variant="outlined" sx={{ borderRadius: 3, border: errors.reason ? '1px solid #ef4444' : '1px solid #e2e8f0' }}>
                                    <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderBottom: '1px solid #fee2e2', display: 'flex', gap: 1 }}>
                                        <RateReview sx={{ color: '#dc2626' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                                            {formData.status === 'Hold' ? 'Hold Reason' : 'Re-Open Reason'} <span style={{ color: '#ef4444' }}>*</span>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <TextField
                                            fullWidth multiline rows={2} name="reason"
                                            error={errors.reason} helperText={errors.reason ? "Reason is mandatory" : ""}
                                            value={formData.reason} onChange={handleInputChange}
                                            placeholder="Provide the reason for this status change..."
                                        />
                                    </Box>
                                </Card>
                            )}

                            {/* REASON HISTORY LIST (Fetched from response) */}
                            {taskData.reasons && taskData.reasons.length > 0 && (
                                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 1 }}>
                                        <HistoryEdu sx={{ color: '#64748b' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', lineHeight: 2 }}>
                                            Status Change History ({taskData.reasons.length})
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <Stack spacing={2}>
                                            {taskData.reasons.map((r, i) => (
                                                <Box key={i} sx={{ p: 1.5, bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 2 }}>
                                                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                                                            ENTRY #{taskData.reasons.length - i}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                            {formatDate(r.createdAt)}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500 }}>
                                                        {r.reason}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Card>
                            )}

                            {/* Description Card */}
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 1 }}>
                                    <Description sx={{ color: '#0369a1' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0369a1', lineHeight: 2 }}>Description</Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{taskData.description || 'No description provided.'}</Typography>
                                </Box>
                            </Card>

                            {/* Comments Section */}
                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'flex', gap: 1 }}>
                                    <ChatBubbleOutline sx={{ color: '#64748b' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 2 }}>Comments ({taskData.comments?.length || 0})</Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    {isEditing && (
                                        <TextField fullWidth multiline rows={3} name="comment" value={formData.comment} onChange={handleInputChange} placeholder="Add a comment..." sx={{ mb: 2 }} />
                                    )}
                                    {taskData.comments?.length > 0 ? (
                                        <Stack spacing={2.5}>
                                            {taskData.comments.map((c, i) => (
                                                <Stack key={i} direction="row" spacing={2}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                                                        {c.first_name?.charAt(0)}
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.first_name} {c.last_name}</Typography>
                                                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{formatDate(c.createdAt)}</Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>{c.comment}</Typography>
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    ) : <Typography variant="body2" align="center" color="textSecondary">No comments yet.</Typography>}
                                </Box>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* Sidebar */}
                    <Grid size={{ xs: 12, md: 3.5 }}>
                        <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5, position: 'sticky', top: 20 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>Progress</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{isEditing ? (formData.progress || 0) : (taskData.progress || 0)}%</Typography>
                            </Box>
                            {isEditing && (
                                <TextField
                                    fullWidth size="small" type="number" label="Update %"
                                    disabled={['Completed', 'Todo', 'Hold', 'Re Open'].includes(formData.status)}
                                    value={formData.progress ?? ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, progress: Math.min(100, Math.max(0, e.target.value)) }))}
                                    sx={{ mt: 1, mb: 2 }}
                                />
                            )}
                            <Box sx={{ width: '100%', bgcolor: '#e2e8f0', height: 8, borderRadius: 10, mb: 3 }}>
                                <Box sx={{ width: `${isEditing ? formData.progress : taskData.progress}%`, bgcolor: '#3b82f6', height: '100%', borderRadius: 10 }} />
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8', display: 'block', mb: 1 }}>ASSIGNED TO</Typography>
                            {taskData.assignees?.map((p, i) => {
                                // 1. Get the color object from your utility
                                const avatarStyle = getAvatarColor(p.first_name);

                                return (
                                    <Stack key={i} direction="row" spacing={1.5} sx={{ mt: 1.5 }} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                // 2. Access the .bg property
                                                bgcolor: avatarStyle.bg,
                                                color: '#fff',
                                                // 3. Access the .border property for the border style
                                                border: `1px solid ${avatarStyle.border}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            {/* 4. Ensure both initials are shown if possible */}
                                            {`${p.first_name?.charAt(0) || ''}${p.last_name?.charAt(0) || ''}`}
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                            {p.first_name} {p.last_name}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>

            {/* Delete Modal */}
            <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)} PaperProps={{ sx: { borderRadius: '12px' } }} fullWidth maxWidth="sm">
                <DialogTitle>Delete Sub-Task?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{taskData.task_title}</strong>?
                        This action cannot be undone and the task will be permanently removed from the system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isDeleting}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        disabled={isDeleting}
                        sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none', minWidth: 100 }}
                    >
                        {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Delete Task'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default UserSubTaskDetails;